import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
} from '@/components/ui';
import { Copy, Eye, ExternalLink } from 'lucide-react';
import { friendlyMessage } from '@/lib/errors';
import { buildPreviewUrl, type PreviewContentType } from './sfOrigin';
import { useCreatePreviewToken } from './usePreviewToken';

export interface PreviewUrlButtonProps {
  contentType: PreviewContentType;
  /** 영속된 레코드 id(상품/이벤트/팝업). 미저장 신규(없음)면 버튼 비활성. */
  contentId: string | undefined;
  /** 버튼 사이즈/variant 오버라이드(기본 outline/sm). */
  size?: 'sm' | 'md' | 'lg';
  variant?: 'outline' | 'ghost' | 'primary';
  className?: string;
}

/**
 * ID58: 검수용 미리보기 URL 생성 버튼(상품/이벤트/팝업 편집 페이지 공유).
 *
 * 클릭 → POST /admin/v1/preview-tokens → 응답 token + 스토어프론트 origin 으로 최종 URL 직접 조립
 * (BE url host 신뢰 금지, 계약 §ID58) → 모달에 URL 표기 + 즉시 클립보드 복사 + 만료시각 안내.
 *
 * 미저장 신규 콘텐츠(contentId 없음)는 미리보기 대상이 없으므로 버튼을 비활성화한다
 * (먼저 저장하면 draft/inactive 로 영속되어 미리보기 가능).
 */
export const PreviewUrlButton = ({
  contentType,
  contentId,
  size = 'sm',
  variant = 'outline',
  className,
}: PreviewUrlButtonProps) => {
  const { t } = useTranslation('common');
  const create = useCreatePreviewToken();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const disabled = !contentId || create.isPending;

  const copy = async (value: string): Promise<boolean> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch {
      // 비보안 컨텍스트 등에서 clipboard API 실패 → 폴백.
    }
    return false;
  };

  const onClick = () => {
    if (!contentId) return;
    setError(null);
    create.mutate(
      { contentType, contentId },
      {
        onSuccess: async (data) => {
          // BE url host(localhost) 무시 — token + SF origin 으로 직접 조립.
          const finalUrl = buildPreviewUrl(contentType, data.token);
          setUrl(finalUrl);
          setExpiresAt(data.expiresAt);
          setOpen(true);
          const copied = await copy(finalUrl);
          if (copied) {
            toast.success(t('cms.preview.copied', { defaultValue: '미리보기 URL을 클립보드에 복사했습니다.' }));
          }
        },
        onError: (e) => {
          setError(friendlyMessage(e, t('cms.preview.createFailed', { defaultValue: '미리보기 URL 생성에 실패했습니다.' })));
          setOpen(true);
        },
      },
    );
  };

  const onCopyClick = async () => {
    const copied = await copy(url);
    toast[copied ? 'success' : 'error'](
      copied
        ? t('cms.preview.copied', { defaultValue: '미리보기 URL을 클립보드에 복사했습니다.' })
        : t('cms.preview.copyFailed', { defaultValue: '복사에 실패했습니다. URL을 직접 선택해 복사하세요.' }),
    );
  };

  // 만료시각을 사용자 로캘로 표기(파싱 실패 시 원문).
  const expiresLabel = (() => {
    if (!expiresAt) return '';
    const d = new Date(expiresAt);
    return Number.isNaN(d.getTime()) ? expiresAt : d.toLocaleString();
  })();

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={onClick}
        disabled={disabled}
        {...(className !== undefined ? { className } : {})}
        {...(!contentId
          ? { title: t('cms.preview.saveFirst', { defaultValue: '먼저 저장하면 미리보기를 생성할 수 있습니다.' }) }
          : {})}
      >
        <Eye size={14} aria-hidden="true" className="mr-1.5" />
        {create.isPending
          ? t('cms.preview.generating', { defaultValue: '생성 중…' })
          : t('cms.preview.generate', { defaultValue: '미리보기 URL 생성' })}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.preview.title', { defaultValue: '미리보기 URL' })}</DialogTitle>
            <DialogDescription>
              {t('cms.preview.desc', {
                defaultValue: '아래 링크로 공개 전 콘텐츠를 검수할 수 있습니다. 링크는 만료 시각 이후 무효화됩니다.',
              })}
            </DialogDescription>
          </DialogHeader>

          {error ? (
            <div
              role="alert"
              className="rounded-md p-3 text-sm"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
            >
              {error}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Input value={url} readOnly aria-label={t('cms.preview.title', { defaultValue: '미리보기 URL' })} className="font-mono text-[12px]" />
                <Button type="button" variant="outline" size="sm" onClick={onCopyClick} aria-label={t('cms.preview.copy', { defaultValue: '복사' })}>
                  <Copy size={14} aria-hidden="true" />
                </Button>
              </div>
              {expiresLabel ? (
                <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                  {t('cms.preview.expiresLabel', { defaultValue: '만료' })}: {expiresLabel}
                </p>
              ) : null}
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit items-center gap-1.5 text-[13px] font-medium"
                style={{ color: 'var(--accent-default)' }}
              >
                <ExternalLink size={14} aria-hidden="true" />
                {t('cms.preview.openInNewTab', { defaultValue: '새 탭에서 열기' })}
              </a>
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.close', { defaultValue: '닫기' })}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
